import { useContext, useState } from 'react'
import { Circle, ExpandLess, ExpandMore } from '@mui/icons-material'
import { NavLink, useLocation } from 'react-router-dom'
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material'

// others
import palette from '../../theme/palette'
import { images } from '../../assets/images'
import { HCKLogo } from '../../assets/svgs'
import { AuthContext } from '../../context'
import { DrawerListItemProps } from '../../interfaceTypes'
import { getNavItems, getSettingsItem } from '../../utils'
import { DrawerWidthCollapsed, DrawerWidthExpanded } from '../../constants'

const DesktopDrawer = ({ sidebarOpen, version }: { sidebarOpen: boolean; version?: string | null }) => {
  const { isAdmin, isAttorney, isParalegal, isInvestigator, isStaff, isAccountant, isSuperAdmin } =
    useContext(AuthContext)

  const location = useLocation()

  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  const handleExpandClick = (itemLabel: string) => {
    setExpandedItems(prev => (prev.has(itemLabel) ? new Set() : new Set([itemLabel])))
  }

  const navItems = getNavItems(
    isParalegal,
    isAttorney,
    isInvestigator,
    isAdmin,
    isStaff,
    isAccountant,
    isSuperAdmin
  )
  const settingsItems = getSettingsItem(isParalegal, isAttorney, isInvestigator, isAdmin, isSuperAdmin)

  const getBasePath = (path: string): string => {
    if (!path) return ''
    const segments = path.split('/').filter(Boolean)
    if (segments.length > 0) {
      return `/${segments[0]}`
    }
    return ''
  }

  //  Helper function to check if a path or any of its sub-items match the current location
  const isPathActive = (item: DrawerListItemProps, currentPath: string, isSubItem = false): boolean => {
    if (!item.path) return false

    // Strip query params for pathname-only comparison
    const itemPathname = item.path.split('?')[0]

    // For sub-items (leaf nodes), only check exact match or startsWith - no base path matching
    if (isSubItem || !item.subItems || item.subItems.length === 0) {
      return currentPath === itemPathname || currentPath.startsWith(itemPathname + '/')
    }

    // 1. Check if current path exactly matches or starts with the item path
    const pathMatch = currentPath === itemPathname || currentPath.startsWith(itemPathname + '/')

    // 2. Check if they share the same base path segment (for detail pages )
    const itemBasePath = getBasePath(itemPathname)
    const currentBasePath = getBasePath(currentPath)
    const basePathMatch = itemBasePath && currentBasePath === itemBasePath && currentPath.startsWith(itemBasePath + '/')

    // 3. Recursively check sub-items
    const subItemMatch = item.subItems?.some(subItem => isPathActive(subItem, currentPath, true)) || false

    return pathMatch || basePathMatch || subItemMatch
  }

  const DrawerListItem = ({ label, path, icon, visible, onClick, isActive, hasSubItems }: DrawerListItemProps) => {
    if (!visible) return null

    const isSubItem = !hasSubItems
    // Strip query params before comparing with location.pathname
    const pathnamePart = path ? path.split('?')[0] : ''
    const isCurrentPathActive = pathnamePart
      ? location.pathname === pathnamePart || location.pathname.startsWith(pathnamePart + '/')
      : false

    return (
      <ListItemButton
        key={path || label}
        component={path ? NavLink : 'div'}
        to={path || ''}
        onClick={onClick}
        sx={{
          py: 1.5,
          px: 2,
          borderLeft: theme =>
            !isSubItem && (isCurrentPathActive || isActive)
              ? `4px solid ${theme.palette.primary.main}`
              : '4px solid transparent',
          '&.Mui-selected': {
            backgroundColor: theme => (!isSubItem ? theme.palette.primary.main : null)
          },
          backgroundColor: theme => (!isSubItem ? (isCurrentPathActive || isActive ? '#FFE9EB' : null) : null)
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: sidebarOpen ? 2 : 'auto',
            justifyContent: 'center',
            color: theme => (isCurrentPathActive || isActive ? theme.palette.primary.main : null)
          }}
        >
          {icon}
        </ListItemIcon>

        {sidebarOpen && (
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              sx: {
                fontWeight: isCurrentPathActive || isActive ? 'bold' : 'normal',
                color: isCurrentPathActive || isActive ? palette.primary.main : 'inherit'
              }
            }}
          />
        )}

        {hasSubItems && sidebarOpen && (expandedItems.has(label) ? <ExpandLess /> : <ExpandMore />)}
      </ListItemButton>
    )
  }

  // Recursive renderer for unlimited nesting
  const renderNavItems = (items: DrawerListItemProps[], level = 0) =>
    items.map(item => {
      const isItemActive = isPathActive(item, location.pathname, level > 0)

      // Also check if any direct sub-item matches exactly (for expanding parent)
      const hasActiveSubItem = item.subItems?.some(sub => {
        const subPathname = sub.path ? sub.path.split('?')[0] : ''
        return location.pathname === subPathname || location.pathname.startsWith(subPathname + '/')
      })

      // Auto-expand if a sub-item is active
      if (hasActiveSubItem && !expandedItems.has(item.label)) {
        setExpandedItems(prev => new Set(prev).add(item.label))
      } else {
        !isItemActive && expandedItems.size > 0 && expandedItems.delete(item.label)
      }

      return (
        item.visible && (
          <Box key={item.path || item.label}>
            <DrawerListItem
              label={item.label}
              path={item.path}
              icon={level === 0 ? item.icon : <Circle fontSize="inherit" color="secondary" />}
              visible={item.visible}
              hasSubItems={!!item.subItems}
              onClick={() => item.subItems && handleExpandClick(item.label)}
              isActive={isItemActive}
            />

            {item.subItems && sidebarOpen && (
              <Collapse in={expandedItems.has(item.label)} timeout="auto" unmountOnExit>
                <List sx={{ pl: 3 + level * 2 }}>{renderNavItems(item.subItems, level + 1)}</List>
              </Collapse>
            )}
          </Box>
        )
      )
    })

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: sidebarOpen ? DrawerWidthExpanded : DrawerWidthCollapsed,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        '& .MuiDrawer-paper': {
          width: sidebarOpen ? DrawerWidthExpanded : DrawerWidthCollapsed,
          transition: 'width 0.25s ease-in-out',
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="flex-start">
        {sidebarOpen ? (
          <Box mx={2} mt={2} component="img" src={images.LOGO} alt="logo" sx={{ width: 100, height: 'auto' }} />
        ) : (
          <Box mx={2} mt={2} sx={{ width: 50, height: 'auto' }}>
            <HCKLogo />
          </Box>
        )}
        {version && (
          <Typography variant="caption" sx={{ mx: 2, mt: 0.5, color: 'text.secondary' }}>
            {sidebarOpen ? `v${version}` : `v${version.split('.')[0]}`}
          </Typography>
        )}
      </Box>

      <List>{renderNavItems(navItems)}</List>

      {!isInvestigator && (
        <>
          <Divider />

          <Box mt={2}>
            {sidebarOpen && (
              <Typography
                textTransform={'uppercase'}
                variant="subtitle1"
                sx={{ px: 2, fontWeight: 'bold', color: 'gray' }}
              >
                Settings
              </Typography>
            )}
            <List>{renderNavItems(settingsItems)}</List>
          </Box>
        </>
      )}
    </Drawer>
  )
}

export default DesktopDrawer
